import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSignIn, useClerk, useUser } from '@clerk/clerk-react';
import { resetClerkAuth } from './utils/clerkResetHelper';

function Login() {
  const navigate = useNavigate();
  const { signIn, isLoaded } = useSignIn();
  const { user, isSignedIn, isLoaded: userLoaded } = useUser();
  const clerk = useClerk();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  
  // Forgot password states
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetCodeSent, setResetCodeSent] = useState(false);
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isProcessingReset, setIsProcessingReset] = useState(false);
  const [resetSuccessMessage, setResetSuccessMessage] = useState('');

  // Check if user is already signed in and handle accordingly
  useEffect(() => {
    const handleSession = async () => {
      if (userLoaded && isSignedIn && user) {
        console.log('User already signed in, handling session...');
        await handleExistingSession();
      }
    };
    
    handleSession();
  }, [userLoaded, isSignedIn, user]);

  // Auto-retry login after Clerk reset
  useEffect(() => {
    const autoRetryLogin = async () => {
      if (isLoaded && !isSignedIn) {
        const shouldRetry = sessionStorage.getItem('autoRetryLogin');
        const pendingLogin = sessionStorage.getItem('pendingLogin');
        
        // If we have pending login, show that we're preparing
        if (shouldRetry === 'true' && pendingLogin && !isLoading) {
          setIsResetting(true);
        }
        
        if (shouldRetry === 'true' && pendingLogin) {
          try {
            console.log('🔄 Auto-retrying login after Clerk reset...');
            const { email: savedEmail, password: savedPassword } = JSON.parse(pendingLogin);
            
            // Clear the retry flags
            sessionStorage.removeItem('autoRetryLogin');
            sessionStorage.removeItem('pendingLogin');
            
            // Set the form fields and show loading
            setEmail(savedEmail);
            setPassword(savedPassword);
            setIsLoading(true);
            setIsResetting(false);
            
            // Retry the login
            const result = await signIn?.create({
              identifier: savedEmail,
              password: savedPassword,
            });

            if (result?.status === 'complete') {
              console.log('✅ Auto-retry login successful!');
              await handleExistingSession();
            }
          } catch (retryError) {
            console.error('Auto-retry login failed:', retryError);
            // Clear retry flags and show normal error handling
            sessionStorage.removeItem('autoRetryLogin');
            sessionStorage.removeItem('pendingLogin');
            setIsLoading(false);
          }
        }
      }
    };
    
    autoRetryLogin();
  }, [isLoaded, isSignedIn, signIn]);

  const handleExistingSession = async () => {
    try {
      console.log('🔑 Using existing Clerk session');

      // Store user info for quick access (optional, but useful for display)
      if (user) {
        localStorage.setItem('userEmail', user.emailAddresses[0]?.emailAddress || '');
        localStorage.setItem('userFullName', user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim());
        console.log('👤 User data stored for display:', {
          email: user.emailAddresses[0]?.emailAddress,
          fullName: user.fullName
        });
      }
      
      // Navigate immediately to dashboard using hard navigation
      console.log('🏠 Navigating to existing user dashboard');
      window.location.replace('/home');
    } catch (error) {
      console.error('Error handling existing session:', error);
      setErrorMessage('Session error. Please try logging in again.');
    }
  };

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isLoaded || !signIn) {
      setErrorMessage('Authentication system is loading. Please try again.');
      return;
    }
    
    if (!email.trim() || !password.trim()) {
      setErrorMessage('Please enter both email and password.');
      return;
    }

    setIsLoading(true);
    setErrorMessage('');

    try {
      // Check if there's already an active session
      if (isSignedIn) {
        setErrorMessage('You are already signed in. Redirecting to dashboard...');
        await handleExistingSession();
        return;
      }

      // Attempt to sign in with Clerk
      const result = await signIn.create({
        identifier: email,
        password: password,
      });

      if (result.status === 'complete') {
        console.log('🔑 Clerk sign-in successful');
        
        // Get user data from Clerk (optional, for display purposes)
        const clerkUser = clerk?.user;
        if (clerkUser) {
          // Store user info for quick access (display only, not authentication)
          localStorage.setItem('userEmail', clerkUser.emailAddresses[0]?.emailAddress || '');
          localStorage.setItem('userFullName', clerkUser.fullName || `${clerkUser.firstName || ''} ${clerkUser.lastName || ''}`.trim());
          console.log('👤 User data stored for display:', {
            email: clerkUser.emailAddresses[0]?.emailAddress,
            fullName: clerkUser.fullName
          });
        }
        
        console.log('🏠 Login successful - navigating to dashboard');
        
        // Navigate to dashboard
        try {
          window.location.replace('/home');
        } catch (navError) {
          console.error('Navigation error:', navError);
          // If navigation fails, try alternative method
          window.location.href = '/home';
        }
        
        // Return early to prevent setIsLoading(false) from being called
        return;
        
      } else {
        // Handle incomplete sign-in (might need email verification, etc.)
        setErrorMessage('Sign-in incomplete. Please check your email for verification if this is your first time.');
      }
    } catch (error: any) {
      console.error('Sign-in error:', error);
      
      // Handle the specific "Unable to authenticate this browser" error silently
      if (error.message?.includes('Unable to authenticate this browser') && 
          error.message?.includes('development instance')) {
        console.log('🔧 Clerk browser state needs reset - will retry login after reset...');
        
        // Store credentials for auto-retry after reset
        sessionStorage.setItem('pendingLogin', JSON.stringify({ email, password }));
        sessionStorage.setItem('autoRetryLogin', 'true');
        
        // Show resetting state
        setIsResetting(true);
        setIsLoading(false);
        
        // Reset Clerk state - this will reload the page
        resetClerkAuth();
        return;
      }
      
      // Handle specific Clerk errors
      if (error.errors && error.errors.length > 0) {
        const clerkError = error.errors[0];
        switch (clerkError.code) {
          case 'form_identifier_not_found':
            setErrorMessage('No account found with this email address.');
            break;
          case 'form_password_incorrect':
            setErrorMessage('Incorrect password. Please try again.');
            break;
          case 'form_identifier_exists':
            setErrorMessage('An account with this email already exists.');
            break;
          case 'session_exists':
            setErrorMessage('You are already signed in. Redirecting to dashboard...');
            await handleExistingSession();
            return;
          default:
            setErrorMessage(clerkError.message || 'Login failed. Please try again.');
        }
      } else {
        setErrorMessage('Login failed. Please check your credentials and try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignUpRedirect = () => {
    navigate('/signup');
  };

  // Forgot Password Functions
  const handleForgotPasswordRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isLoaded || !signIn) {
      setErrorMessage('Authentication system is loading. Please try again.');
      return;
    }
    
    if (!email.trim()) {
      setErrorMessage('Please enter your email address.');
      return;
    }

    setIsProcessingReset(true);
    setErrorMessage('');
    setResetSuccessMessage('');

    try {
      // Create sign-in attempt with email
      const signInAttempt = await signIn.create({
        identifier: email,
      });

      // Get the supported first factors and find the reset password email factor
      const supportedFactors = signInAttempt.supportedFirstFactors || [];
      const resetFactor = supportedFactors.find(
        (factor: any) => factor.strategy === "reset_password_email_code"
      ) as any;

      if (!resetFactor) {
        throw new Error('Password reset via email is not available for this account');
      }

      // Extract email address ID safely
      const emailAddressId = resetFactor.emailAddressId || signInAttempt.identifier;

      // Start password reset flow
      await signIn.prepareFirstFactor({
        strategy: "reset_password_email_code",
        emailAddressId: emailAddressId,
      } as any); // Type assertion for Clerk API compatibility

      setResetCodeSent(true);
      setResetSuccessMessage('Reset code sent! Check your email for the verification code.');
      console.log('✅ Password reset code sent successfully');
      
    } catch (error: any) {
      console.error('Password reset request error:', error);
      
      // Handle specific Clerk errors
      if (error.errors && error.errors.length > 0) {
        const clerkError = error.errors[0];
        switch (clerkError.code) {
          case 'form_identifier_not_found':
            setErrorMessage('No account found with this email address.');
            break;
          default:
            setErrorMessage(clerkError.message || 'Failed to send reset code. Please try again.');
        }
      } else {
        setErrorMessage('Failed to send reset code. Please try again.');
      }
    } finally {
      setIsProcessingReset(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isLoaded || !signIn) {
      setErrorMessage('Authentication system is loading. Please try again.');
      return;
    }
    
    if (!resetCode.trim() || !newPassword.trim() || !confirmPassword.trim()) {
      setErrorMessage('Please fill in all fields.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrorMessage('Passwords do not match.');
      return;
    }

    if (newPassword.length < 8) {
      setErrorMessage('Password must be at least 8 characters long.');
      return;
    }

    setIsProcessingReset(true);
    setErrorMessage('');

    try {
      const result = await signIn.attemptFirstFactor({
        strategy: "reset_password_email_code",
        code: resetCode,
        password: newPassword,
      });

      if (result.status === "complete") {
        console.log('✅ Password reset successful');
        setResetSuccessMessage('Password reset successfully! You can now sign in with your new password.');
        
        // Reset all forgot password states
        setShowForgotPassword(false);
        setResetCodeSent(false);
        setResetCode('');
        setNewPassword('');
        setConfirmPassword('');
        
        // Clear the email and password fields for fresh login
        setPassword('');
        
        // Show success message for a moment, then clear it
        setTimeout(() => {
          setResetSuccessMessage('');
        }, 5000);
      }
    } catch (error: any) {
      console.error('Password reset error:', error);
      
      // Handle specific Clerk errors
      if (error.errors && error.errors.length > 0) {
        const clerkError = error.errors[0];
        switch (clerkError.code) {
          case 'form_code_incorrect':
            setErrorMessage('Invalid reset code. Please check your email and try again.');
            break;
          case 'form_password_pwned':
            setErrorMessage('This password has been found in a data breach. Please choose a different password.');
            break;
          case 'form_password_too_common':
            setErrorMessage('This password is too common. Please choose a stronger password.');
            break;
          default:
            setErrorMessage(clerkError.message || 'Failed to reset password. Please try again.');
        }
      } else {
        setErrorMessage('Failed to reset password. Please try again.');
      }
    } finally {
      setIsProcessingReset(false);
    }
  };

  const handleBackToLogin = () => {
    setShowForgotPassword(false);
    setResetCodeSent(false);
    setResetCode('');
    setNewPassword('');
    setConfirmPassword('');
    setErrorMessage('');
    setResetSuccessMessage('');
  };

  // If user is already signed in, show loading while redirecting
  if (userLoaded && isSignedIn) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center" style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif" }}>
        <div className="text-center p-8">
          <div className="w-8 h-8 border-2 border-gray-300 border-t-black rounded-full animate-spin mx-auto mb-4"></div>
          <div className="text-lg text-gray-600 mb-2">
            Already signed in, redirecting to dashboard...
          </div>
          <div className="text-sm text-gray-500">
            Please wait while we redirect you
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4" style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif" }}>
      {/* Subtle animated background elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-gray-300 rounded-full animate-pulse opacity-40"></div>
        <div className="absolute top-3/4 right-1/4 w-1 h-1 bg-gray-400 rounded-full animate-pulse delay-1000 opacity-30"></div>
        <div className="absolute top-1/2 left-1/6 w-1.5 h-1.5 bg-gray-200 rounded-full animate-pulse delay-2000 opacity-50"></div>
        <div className="absolute bottom-1/4 right-1/6 w-2 h-2 bg-gray-300 rounded-full animate-pulse delay-3000 opacity-40"></div>
      </div>
      
      <div className="relative w-full max-w-md z-10">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 md:p-10">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-6">
              <img 
                src="/images/noBgColor.png" 
                alt="Silas Logo" 
                className="h-12 w-auto object-contain transition-transform duration-300 hover:scale-105"
              />
            </div>
            {/* <h1 className="text-2xl md:text-3xl font-bold text-black mb-2" style={{ fontFamily: "'Canela', serif", fontWeight: 700, letterSpacing: '-0.02em' }}>
              Welcome Back
            </h1> */}
            <p className="text-gray-600">
              Sign in to continue your mental health journey
            </p>
          </div>

          {/* Error Messages */}
          {errorMessage && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-red-500 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
                </svg>
                <p className="text-sm text-red-700 font-medium">
                  {errorMessage}
                </p>
              </div>
            </div>
          )}

          {/* Success Messages */}
          {resetSuccessMessage && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.236 4.53L8.23 10.661a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z" clipRule="evenodd" />
                </svg>
                <p className="text-sm text-green-700 font-medium">
                  {resetSuccessMessage}
                </p>
              </div>
            </div>
          )}

          {/* Conditional Forms */}
          {!showForgotPassword ? (
            /* Login Form */
            <form onSubmit={handleEmailSignIn} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (errorMessage) setErrorMessage('');
                  }}
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg 
                             focus:ring-2 focus:ring-gray-400 focus:border-gray-400 
                             transition-all duration-200 bg-white hover:border-gray-400
                             disabled:bg-gray-50 disabled:text-gray-500"
                  placeholder="you@example.com"
                  disabled={isLoading}
                />
              </div>
              
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                    Password
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-xs text-gray-500 hover:text-black transition-colors duration-200"
                  >
                    Forgot password?
                  </button>
                </div>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (errorMessage) setErrorMessage('');
                  }}
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg 
                             focus:ring-2 focus:ring-gray-400 focus:border-gray-400 
                             transition-all duration-200 bg-white hover:border-gray-400
                             disabled:bg-gray-50 disabled:text-gray-500"
                  placeholder="••••••••"
                  disabled={isLoading}
                />
              </div>
              
              <button 
                type="submit" 
                disabled={!email.trim() || !password.trim() || isLoading || isResetting}
                className="w-full bg-black text-white font-medium py-3 px-4 rounded-lg
                           hover:bg-gray-800 focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 
                           transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed
                           shadow-sm hover:shadow-md transform hover:scale-[1.02]
                           disabled:transform-none disabled:hover:bg-black disabled:hover:shadow-sm"
              >
                {isResetting ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-gray-400 border-t-white rounded-full animate-spin"></div>
                    Preparing...
                  </span>
                ) : isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-gray-400 border-t-white rounded-full animate-spin"></div>
                    Signing In...
                  </span>
                ) : (
                  'Sign In'
                )}
              </button>
            </form>
          ) : !resetCodeSent ? (
            /* Forgot Password - Email Form */
            <div className="space-y-6">
              <div className="text-center mb-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-2">Reset Your Password</h2>
                <p className="text-sm text-gray-600">
                  Enter your email address and we'll send you a reset code.
                </p>
              </div>
              
              <form onSubmit={handleForgotPasswordRequest} className="space-y-6">
                <div>
                  <label htmlFor="reset-email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    id="reset-email"
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (errorMessage) setErrorMessage('');
                    }}
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg 
                               focus:ring-2 focus:ring-gray-400 focus:border-gray-400 
                               transition-all duration-200 bg-white hover:border-gray-400
                               disabled:bg-gray-50 disabled:text-gray-500"
                    placeholder="you@example.com"
                    disabled={isProcessingReset}
                  />
                </div>
                
                <button 
                  type="submit" 
                  disabled={!email.trim() || isProcessingReset}
                  className="w-full bg-black text-white font-medium py-3 px-4 rounded-lg
                             hover:bg-gray-800 focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 
                             transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed
                             shadow-sm hover:shadow-md transform hover:scale-[1.02]
                             disabled:transform-none disabled:hover:bg-black disabled:hover:shadow-sm"
                >
                  {isProcessingReset ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-gray-400 border-t-white rounded-full animate-spin"></div>
                      Sending Code...
                    </span>
                  ) : (
                    'Send Reset Code'
                  )}
                </button>
              </form>
              
              <div className="text-center">
                <button
                  type="button"
                  onClick={handleBackToLogin}
                  className="text-sm text-gray-500 hover:text-black transition-colors duration-200"
                >
                  ← Back to Sign In
                </button>
              </div>
            </div>
          ) : (
            /* Forgot Password - Reset Code & New Password Form */
            <div className="space-y-6">
              <div className="text-center mb-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-2">Enter Reset Code</h2>
                <p className="text-sm text-gray-600">
                  We sent a reset code to <span className="font-medium">{email}</span>
                </p>
              </div>
              
              <form onSubmit={handlePasswordReset} className="space-y-6">
                <div>
                  <label htmlFor="reset-code" className="block text-sm font-medium text-gray-700 mb-2">
                    Reset Code
                  </label>
                  <input
                    id="reset-code"
                    type="text"
                    value={resetCode}
                    onChange={(e) => {
                      setResetCode(e.target.value);
                      if (errorMessage) setErrorMessage('');
                    }}
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg 
                               focus:ring-2 focus:ring-gray-400 focus:border-gray-400 
                               transition-all duration-200 bg-white hover:border-gray-400
                               disabled:bg-gray-50 disabled:text-gray-500"
                    placeholder="Enter the code from your email"
                    disabled={isProcessingReset}
                  />
                </div>
                
                <div>
                  <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 mb-2">
                    New Password
                  </label>
                  <input
                    id="new-password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value);
                      if (errorMessage) setErrorMessage('');
                    }}
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg 
                               focus:ring-2 focus:ring-gray-400 focus:border-gray-400 
                               transition-all duration-200 bg-white hover:border-gray-400
                               disabled:bg-gray-50 disabled:text-gray-500"
                    placeholder="Enter your new password"
                    disabled={isProcessingReset}
                  />
                </div>
                
                <div>
                  <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm New Password
                  </label>
                  <input
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      if (errorMessage) setErrorMessage('');
                    }}
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg 
                               focus:ring-2 focus:ring-gray-400 focus:border-gray-400 
                               transition-all duration-200 bg-white hover:border-gray-400
                               disabled:bg-gray-50 disabled:text-gray-500"
                    placeholder="Confirm your new password"
                    disabled={isProcessingReset}
                  />
                </div>
                
                <button 
                  type="submit" 
                  disabled={!resetCode.trim() || !newPassword.trim() || !confirmPassword.trim() || isProcessingReset}
                  className="w-full bg-black text-white font-medium py-3 px-4 rounded-lg
                             hover:bg-gray-800 focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 
                             transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed
                             shadow-sm hover:shadow-md transform hover:scale-[1.02]
                             disabled:transform-none disabled:hover:bg-black disabled:hover:shadow-sm"
                >
                  {isProcessingReset ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-gray-400 border-t-white rounded-full animate-spin"></div>
                      Resetting Password...
                    </span>
                  ) : (
                    'Reset Password'
                  )}
                </button>
              </form>
              
              <div className="text-center">
                <button
                  type="button"
                  onClick={handleBackToLogin}
                  className="text-sm text-gray-500 hover:text-black transition-colors duration-200"
                >
                  ← Back to Sign In
                </button>
              </div>
            </div>
          )}

          {/* Sign Up Link - Only show when not in forgot password mode */}
          {!showForgotPassword && (
            <div className="mt-8 text-center">
              <p className="text-gray-600 text-sm mb-3">
                Don't have an account?
              </p>
              <button
                type="button"
                onClick={handleSignUpRedirect}
                className="text-black hover:text-gray-700 font-medium underline transition-colors duration-200 text-sm"
              >
                Create an account
              </button>
            </div>
          )}

          {/* Additional Info */}
          <div className="mt-8 pt-6 border-t border-gray-100">
            <div className="flex items-center justify-center gap-2 text-gray-500 text-xs">
              <div className="w-4 h-4 bg-gray-200 rounded flex items-center justify-center">
                <div className="w-2 h-2 border border-gray-400 rounded-sm"></div>
              </div>
              <span>Secure & Private</span>
            </div>
          </div>
        </div>

        {/* Back to Home Link */}
        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/')}
            className="text-gray-500 hover:text-black transition-colors duration-200 text-sm flex items-center justify-center gap-2 mx-auto group"
          >
            <span className="transform group-hover:-translate-x-1 transition-transform duration-300">←</span>
            Back to Home
          </button>
        </div>
      </div>
    </div>
  );
}

export default Login; 